import moment from 'moment'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import Form from 'react-bootstrap/Form'
import 'react-datepicker/dist/react-datepicker.css'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import Select from 'react-select'
import filterSolidIcon from '../../assets/images/filter-solid-icon.svg'
import ScrollToggle from '../../components/ScrollToggle/ScrollToggle'
import { MONTHS } from '../../resources/constants'
import { SalesTransSearchFormI } from '../../resources/form-props'
import { calculateMaxHeightContent, lastYears } from '../../resources/functions'
import { useTransTableDataApi } from '../../resources/hooks/api/transApiHook'
import { getBranch } from '../../store/user/selectors'
import './filter.css'

interface Props {}

type SortField = 'date' | 'grossSale' | 'stateSale'
type SortDirection = 'asc' | 'desc'

interface SortConfig {
  field: SortField | null
  direction: SortDirection | null
}

interface TableRowModel {
  originalIndex: number
  rec: any
  transactionTimestamp: number
  grossSaleValue: number
  stateSaleValue: number
  postedValue: string
}

const parseTransactionDate = (value: any) => {
  if (!value) {
    return 0
  }

  const isoDate = moment.utc(value, moment.ISO_8601, true)

  if (isoDate.isValid()) {
    return isoDate.valueOf()
  }

  const formattedDate = moment.utc(
    value,
    ['MM-DD-YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY'],
    true
  )

  if (formattedDate.isValid()) {
    return formattedDate.valueOf()
  }

  const fallbackDate = moment.utc(value)

  return fallbackDate.isValid() ? fallbackDate.valueOf() : 0
}

const parseAmountValue = (value: any) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }

  const parsedValue = parseFloat(String(value ?? '').replace(/[^0-9.-]/g, ''))

  return Number.isNaN(parsedValue) ? 0 : parsedValue
}

const amountNumberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const formatCurrencyAmount = (value: any) =>
  `$ ${amountNumberFormatter.format(Math.abs(parseAmountValue(value)))}`

const HeaderIcon = ({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="currentColor"
    aria-hidden="true"
  >
    {children}
  </svg>
)

const SortIndicatorIcon = ({
  direction,
}: {
  direction: SortDirection | null
}) => {
  if (direction === 'asc') {
    return (
      <HeaderIcon className="table-header-icon">
        <path
          fillRule="evenodd"
          d="M8 12a.5.5 0 0 0 .5-.5V3.707l2.146 2.147a.5.5 0 0 0 .708-.708l-3-3a.5.5 0 0 0-.708 0l-3 3a.5.5 0 1 0 .708.708L7.5 3.707V11.5A.5.5 0 0 0 8 12z"
        />
      </HeaderIcon>
    )
  }

  if (direction === 'desc') {
    return (
      <HeaderIcon className="table-header-icon">
        <path
          fillRule="evenodd"
          d="M8 4a.5.5 0 0 0-.5.5v7.793L5.354 10.146a.5.5 0 1 0-.708.708l3 3a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 12.293V4.5A.5.5 0 0 0 8 4z"
        />
      </HeaderIcon>
    )
  }

  return (
    <HeaderIcon className="table-header-icon table-header-icon--muted">
      <path
        fillRule="evenodd"
        d="M11.5 15a.5.5 0 0 0 .5-.5V2.707l1.146 1.147a.5.5 0 0 0 .708-.708l-2-2a.5.5 0 0 0-.708 0l-2 2a.5.5 0 1 0 .708.708L11 2.707V14.5a.5.5 0 0 0 .5.5zm-7-14a.5.5 0 0 0-.5.5v11.793L2.854 12.146a.5.5 0 1 0-.708.708l2 2a.5.5 0 0 0 .708 0l2-2a.5.5 0 1 0-.708-.708L5 13.293V1.5a.5.5 0 0 0-.5-.5z"
      />
    </HeaderIcon>
  )
}

const FilterIndicatorIcon = ({ active }: { active: boolean }) => (
  <HeaderIcon
    className={`table-header-icon${
      active ? ' table-header-icon--active' : ' table-header-icon--muted'
    }`}
  >
    <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .39.812L10 7.333V12.5a.5.5 0 0 1-.223.416l-2 1.5A.5.5 0 0 1 7 14V7.333L1.61 1.812A.5.5 0 0 1 1.5 1.5z" />
  </HeaderIcon>
)

const SaleTransaction: React.FC<Props> = () => {
  const { t } = useTranslation('scenes.transactions.salesTransactions')
  const { fetchBankTransData } = useTransTableDataApi()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [data, setData] = useState<any>([])
  const [filterData, setFilterData] = useState<any>({
    year: 2024,
    month: 1,
    trans_type: 1,
  })
  const [contentMaxHeight, setContentMaxHeight] = useState<number>(0)
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: null,
    direction: null,
  })
  const [activeFilterPopup, setActiveFilterPopup] = useState<'posted' | null>(
    null
  )
  const [postedFilterValues, setPostedFilterValues] = useState<string[]>([])
  const postedFilterRef = useRef<HTMLDivElement | null>(null)
  const tableContainerRef = useRef<HTMLDivElement | null>(null)

  const [currentPage] = useState<any>(1)
  const [recordPerPage] = useState<any>(5000)
  const branchData = useSelector(getBranch)

  const {
    control: control2,
    handleSubmit: handleSubmit2,
    formState: { errors: errors2 },
  } = useForm<SalesTransSearchFormI>({
    defaultValues: {
      month: {
        value: '01',
        label:  t('month.01', { ns: 'resources.form-validator' }),
      },
      year: { value: '2024', label: '2024' },
    },
  })

  useEffect(() => {
    fetchTransactionList()
  }, [filterData])

  useEffect(() => {
    setContentMaxHeight(calculateMaxHeightContent(78))
  })

  useEffect(() => {
    if (!activeFilterPopup) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      if (
        activeFilterPopup === 'posted' &&
        postedFilterRef.current &&
        !postedFilterRef.current.contains(target)
      ) {
        setActiveFilterPopup(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [activeFilterPopup])

  useEffect(() => {
    setActiveFilterPopup(null)
  }, [data])

  const fetchTransactionList = () => {
    const params: any = {
      page: currentPage,
      per_page: recordPerPage,
      year: +filterData['year'],
      month: +filterData['month'],
      account_type: 2,
      branch_id: +branchData['id'],
    }

    setIsLoading(true)

    fetchBankTransData(
      params,
      (message: string, resp: any) => {
        const result = resp.data.data.result
        const pushRec: any[] = []

        result.forEach((rec: any) => {
          const grossSale = rec['breakdowns'].filter(
            (e: any) => e['account_type'] == 'Gross_Sale'
          )
          const stateTax = rec['breakdowns'].filter(
            (e: any) => e['account_type'] == 'State_Tax'
          )

          pushRec.push({
            parent_id: rec.parent_id,
            description: rec.description,
            account_name: rec.account_name,
            transaction_at: rec.transaction_at,
            state_tax:
              (stateTax.length && stateTax[0]['account_value']) || 0,
            gross_sale:
              (grossSale.length && grossSale[0]['account_value']) || 0,
            posted: rec.updated_at ? 'YES' : 'NO',
          })
        })

        setData(pushRec)
        setIsLoading(false)
      },
      (message: string) => {
        setIsLoading(false)
        toast.error(message)
      }
    )
  }

  const processSearchData = (searchData: any) => {
    setFilterData({
      year: +searchData['year']['value'],
      month: +searchData['month']['value'],
    })
  }

  const tableRows = useMemo<TableRowModel[]>(
    () =>
      data.map((rec: any, index: number) => ({
        originalIndex: index,
        rec,
        transactionTimestamp: parseTransactionDate(rec.transaction_at),
        grossSaleValue: Math.abs(parseAmountValue(rec.gross_sale)),
        stateSaleValue: Math.abs(parseAmountValue(rec.state_tax)),
        postedValue:
          `${rec.posted ?? ''}`.trim().toUpperCase() === 'YES' ? 'YES' : 'NO',
      })),
    [data]
  )

  const uniquePostedValues = useMemo(
    () => Array.from(new Set(tableRows.map((row) => row.postedValue))),
    [tableRows]
  )

  const hasMultiplePostedValues = uniquePostedValues.length > 1
  const isPostedFilterActive =
    hasMultiplePostedValues &&
    postedFilterValues.length > 0 &&
    postedFilterValues.length < uniquePostedValues.length

  useEffect(() => {
    if (hasMultiplePostedValues) {
      return
    }

    setPostedFilterValues((previousValues) =>
      previousValues.length ? [] : previousValues
    )

    if (activeFilterPopup === 'posted') {
      setActiveFilterPopup(null)
    }
  }, [activeFilterPopup, hasMultiplePostedValues])

  const displayedRows = useMemo(() => {
    let nextRows = tableRows

    if (postedFilterValues.length > 0) {
      nextRows = nextRows.filter((row) =>
        postedFilterValues.includes(row.postedValue)
      )
    }

    if (!sortConfig.field || !sortConfig.direction) {
      return nextRows
    }

    const getSortValue = (row: TableRowModel) => {
      switch (sortConfig.field) {
        case 'date':
          return row.transactionTimestamp
        case 'grossSale':
          return row.grossSaleValue
        case 'stateSale':
          return row.stateSaleValue
        default:
          return row.originalIndex
      }
    }

    return [...nextRows].sort((left, right) => {
      const leftValue = getSortValue(left)
      const rightValue = getSortValue(right)

      if (leftValue === rightValue) {
        return left.originalIndex - right.originalIndex
      }

      return sortConfig.direction === 'asc'
        ? leftValue - rightValue
        : rightValue - leftValue
    })
  }, [postedFilterValues, sortConfig, tableRows])

  const displayTotals = useMemo(
    () =>
      displayedRows.reduce(
        (totals, row) => {
          totals.grossSale += row.grossSaleValue
          totals.stateSale += row.stateSaleValue

          return totals
        },
        {
          grossSale: 0,
          stateSale: 0,
        }
      ),
    [displayedRows]
  )

  const toggleSort = (field: SortField) => {
    setSortConfig((previousSort) => {
      if (field === 'date') {
        if (!previousSort.field && !previousSort.direction) {
          return {
            field,
            direction: 'desc',
          }
        }

        if (previousSort.field === 'date') {
          return {
            field,
            direction: previousSort.direction === 'desc' ? 'asc' : 'desc',
          }
        }

        return {
          field,
          direction: 'asc',
        }
      }

      if (previousSort.field !== field) {
        return {
          field,
          direction: 'asc',
        }
      }

      if (previousSort.direction === 'asc') {
        return {
          field,
          direction: 'desc',
        }
      }

      return {
        field: null,
        direction: null,
      }
    })
  }

  const getSortDirection = (field: SortField) =>
    field === 'date' && !sortConfig.field && !sortConfig.direction
      ? 'asc'
      : sortConfig.field === field
      ? sortConfig.direction
      : null

  const toggleFilterPopup = () => {
    setActiveFilterPopup((currentPopup) =>
      currentPopup === 'posted' ? null : 'posted'
    )
  }

  const togglePostedFilter = (value: string) => {
    setPostedFilterValues((previousValues) =>
      previousValues.includes(value)
        ? previousValues.filter((item) => item !== value)
        : [...previousValues, value]
    )
  }

  const resetPostedFilter = () => {
    setPostedFilterValues([])
    setActiveFilterPopup(null)
  }

  return (
    <div className="main-container flex-grow-1">
      <div className="container-fluid">
         <div className="d-flex justify-content-between align-items-center flex-wrap pb-3 pt-2">
            <div>
              <h1 className="h5 font-weight-700 mb-0 d-inline-flex text-black">
                {t('title')}
              </h1>
            </div>
                <Form>
            <div className="d-flex align-items-center gap-2 flex-wrap">
                      <Form.Group className="filter-box" controlId="formBasicEmail">
                        <Controller
                          name="month"
                          control={control2}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={MONTHS}
                              className={'zindex-10'}
                            />
                          )}
                        />
                        {(errors2 &&
                          errors2['month'] &&
                          errors2['month'].message && (
                            <Form.Text className="text-danger">
                              {(errors2['month'].message as ReactNode) || ''}
                            </Form.Text>
                          )) ||
                          ''}
                      </Form.Group>

                      <Form.Group className="filter-box" controlId="formBasicEmail">
                        <Controller
                          name="year"
                          control={control2}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={lastYears(5)}
                              className={'zindex-10'}
                            />
                          )}
                        />
                        {(errors2 &&
                          errors2['year'] &&
                          errors2['year'].message && (
                            <Form.Text className="text-danger">
                              {(errors2['year'].message as ReactNode) || ''}
                            </Form.Text>
                          )) ||
                          ''}
                      </Form.Group>

                      <div className="filter-btn">
                        <button
                          type="button"
                          onClick={handleSubmit2(processSearchData)}
                        >
                          <img src={filterSolidIcon} alt="" /> {t('button.filter')}
                        </button>
                      </div>
                      <div className="filter-btn">
                        <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
                          <div className="loader"></div>
                        </div>
                      </div>
              </div>
                </Form>
          </div>
        <div className=" executive-dashboard">
          <div className="dashboard-top-part">
          

            <div
              className="table-responsive list-table table-scroll"
              style={{ maxHeight: 'calc(100vh - 220px)' ,overflowY: 'auto'}}
              ref={tableContainerRef}
            >
              <table className="table table-default">
                <thead>
                  <tr className="align-middle">
                    <th>{t('table.number')}</th>
                    <th className="text-center">
                      <button
                        type="button"
                        className="table-header-sort-btn table-header-sort-btn--center"
                        onClick={() => toggleSort('date')}
                      >
                        <span>{t('table.date')}</span>
                        <SortIndicatorIcon direction={getSortDirection('date')} />
                      </button>
                    </th>
                    <th className="text-end">
                      <button
                        type="button"
                        className="table-header-sort-btn table-header-sort-btn--end"
                        onClick={() => toggleSort('grossSale')}
                      >
                        <span>{t('table.grossSale')}</span>
                        <SortIndicatorIcon
                          direction={getSortDirection('grossSale')}
                        />
                      </button>
                    </th>
                    <th className="text-end">
                      <button
                        type="button"
                        className="table-header-sort-btn table-header-sort-btn--end"
                        onClick={() => toggleSort('stateSale')}
                      >
                        <span>{t('table.stateSale')}</span>
                        <SortIndicatorIcon
                          direction={getSortDirection('stateSale')}
                        />
                      </button>
                    </th>
                    <th className="text-center">
                      <div
                        className="table-header-filter-wrap table-header-filter-wrap--center"
                        ref={postedFilterRef}
                      >
                        <span>{t('table.posted')}</span>
                        {hasMultiplePostedValues ? (
                          <>
                            <button
                              type="button"
                              className={`table-header-icon-btn${
                                isPostedFilterActive ? ' active' : ''
                              }`}
                              onClick={toggleFilterPopup}
                              aria-label={`Filter ${t('table.posted')}`}
                              aria-expanded={activeFilterPopup === 'posted'}
                            >
                              <FilterIndicatorIcon active={isPostedFilterActive} />
                            </button>
                            {activeFilterPopup === 'posted' ? (
                              <div className="table-filter-popup table-filter-popup--posted">
                                <div className="table-filter-popup__checklist">
                                  {[
                                    { label: t('option.yes'), value: 'YES' },
                                    { label: t('option.no'), value: 'NO' },
                                  ].map((option) => (
                                    <label
                                      key={option.value}
                                      className="table-filter-option"
                                    >
                                      <input
                                        type="checkbox"
                                        className="form-check-input m-0"
                                        checked={postedFilterValues.includes(
                                          option.value
                                        )}
                                        onChange={() =>
                                          togglePostedFilter(option.value)
                                        }
                                      />
                                      <span>{option.label}</span>
                                    </label>
                                  ))}
                                </div>
                                <button
                                  type="button"
                                  className="table-filter-popup__reset"
                                  onClick={resetPostedFilter}
                                >
                                  {t('button.reset')}
                                </button>
                              </div>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayedRows.length > 0 ? (
                    displayedRows.map((row, index: number) => {
                      const rec = row.rec

                      return (
                        <tr
                          className="align-middle h-60"
                          key={`${rec.parent_id || 'row'}-${row.originalIndex}`}
                        >
                          <td>{index + 1}</td>
                          <td className="text-center">
                            <strong>
                              {row.transactionTimestamp
                                ? moment.utc(row.transactionTimestamp).format(
                                    'MM-DD-YYYY'
                                  )
                                : ''}
                            </strong>
                          </td>
                          <td className="text-end">
                            {formatCurrencyAmount(rec.gross_sale)}
                          </td>
                          <td className="text-end">
                            {formatCurrencyAmount(rec.state_tax)}
                          </td>
                          <td className="text-center">{row.postedValue}</td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center">
                        {t('message.noData')}
                      </td>
                    </tr>
                  )}
                </tbody>
                {displayedRows.length > 0 ? (
                  <tfoot>
                    <tr className="align-middle h-60 table-sticky-total-row">
                      <td></td>
                      <td className="text-right">
                        <strong>{t('table.total')}</strong>
                      </td>
                      <td className="text-end">
                        {formatCurrencyAmount(displayTotals.grossSale)}
                      </td>
                      <td className="text-end">
                        {formatCurrencyAmount(displayTotals.stateSale)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
              <ScrollToggle scrollRef={tableContainerRef} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SaleTransaction
